import { useEffect, useState } from 'react'
import { User, Save, Download, LogIn, LogOut, Shield } from 'lucide-react'
import { profileApi, type Profile } from '../services/api'
import { useAuth } from '../contexts/useAuth'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const Stepper = ({ label, description, value, min, max, onChange }: { label: string; description: string; value: number; min: number; max: number; onChange: (v: number) => void }) => (
    <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-3)' }}>{description}</div>
        </div>
        <div className="stepper-full">
            <button className="stepper-btn" onClick={() => onChange(Math.max(min, value - 1))}>−</button>
            <span className="stepper-val" style={{ flex: 1, padding: '12px 0', textAlign: 'center', fontSize: '1.2rem', fontWeight: 700 }}>{value}</span>
            <button className="stepper-btn" onClick={() => onChange(Math.min(max, value + 1))}>+</button>
        </div>
    </div>
)

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[]
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed'
        platform: string
    }>
    prompt(): Promise<void>
}

export default function Profile() {
    const [profile, setProfile] = useState<Profile>({ nb_persons: 2, meals_per_day: 2 })
    const [loading, setLoading] = useState(true)
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
    const { user, isAuthenticated, loading: authLoading, loginWithGoogle, logout } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault()
            setDeferredPrompt(e as BeforeInstallPromptEvent)
        }
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

        const handleAppInstalled = () => {
            setDeferredPrompt(null)
            toast.success('Application installée !')
        }
        window.addEventListener('appinstalled', handleAppInstalled)

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
            window.removeEventListener('appinstalled', handleAppInstalled)
        }
    }, [])

    useEffect(() => {
        if (authLoading) return

        let active = true
        Promise.resolve().then(() => {
            if (active) setLoading(true)
        })
        profileApi.get()
            .then(p => {
                if (active) setProfile(p)
            })
            .catch(() => { })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [isAuthenticated, authLoading])

    const save = async () => {
        await profileApi.update(profile)
        toast.success('Profil mis à jour !')
    }

    return (
        <div>
            <div className="page-header">
                <User size={24} color="var(--primary-light)" />
                <h1>Mon profil</h1>
            </div>

            {/* Auth section */}
            <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
                {isAuthenticated && user ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: '50%',
                            background: 'var(--primary-gradient)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0,
                        }}>
                            {user.full_name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{user.full_name}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => { logout(); navigate('/') }} style={{ flexShrink: 0 }}>
                            <LogOut size={15} /> Déconnexion
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Shield size={20} color="var(--text-3)" />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Mode invité</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>Connectez-vous pour accéder à l'inventaire et l'IA</div>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={loginWithGoogle} style={{ flexShrink: 0 }}>
                            <LogIn size={15} /> Connexion
                        </button>
                    </div>
                )}
            </div>

            {!loading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Stepper
                        label="Nombre de personnes"
                        description="Personnes dans votre foyer (utilisé par défaut pour les calculs)"
                        value={profile.nb_persons}
                        min={1} max={20}
                        onChange={v => setProfile(p => ({ ...p, nb_persons: v }))}
                    />
                    <Stepper
                        label="Repas par jour"
                        description="Nombre de repas principaux par jour (déjeuner + dîner = 2)"
                        value={profile.meals_per_day}
                        min={1} max={5}
                        onChange={v => setProfile(p => ({ ...p, meals_per_day: v }))}
                    />

                    {/* Explanation card */}
                    <div className="info-card" style={{ padding: '16px 20px' }}>
                        <p style={{ fontSize: '0.85rem', color: 'var(--primary-light)', fontWeight: 600, marginBottom: 8 }}>Formule de calcul</p>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', lineHeight: 1.7 }}>
                            <code style={{ background: 'rgba(99, 102, 241, 0.12)', padding: '2px 8px', borderRadius: 6, fontSize: '0.78rem', color: 'var(--primary-light)' }}>
                                portions = (durée × repas/jour × personnes) / nb_plats
                            </code>
                            <br />
                            Exemple : 7 jours, {profile.meals_per_day} repas/jour, {profile.nb_persons} personnes, 3 plats → <strong style={{ color: 'var(--primary-light)' }}>{Math.round(7 * profile.meals_per_day * profile.nb_persons / 3 * 10) / 10}</strong> portions / plat
                        </p>
                    </div>

                    <button className="btn btn-primary btn-full" onClick={save} style={{ marginTop: 4 }}>
                        <Save size={16} /> Enregistrer
                    </button>


                    {/* PWA Install Button */}
                    {deferredPrompt && (
                        <button
                            className="theme-toggle"
                            style={{ borderColor: 'var(--primary)', background: 'var(--primary-glow)' }}
                            onClick={() => {
                                deferredPrompt.prompt()
                                deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
                                    if (choiceResult.outcome === 'accepted') {
                                        setDeferredPrompt(null)
                                    }
                                })
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--primary-light)', fontWeight: 600 }}>
                                <Download size={18} />
                                Installer l'application
                            </span>
                            <span style={{ fontSize: '0.78rem', color: 'var(--primary)' }}>Rapide</span>
                        </button>
                    )}

                    {/* App info */}
                    <div className="divider" />
                    <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: '0.8rem' }}>
                        <p className="hero-gradient" style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>EasyMarket</p>
                        <p>v1.0.0 — Application de liste de courses intelligente</p>
                    </div>
                </div>
            )}
        </div>
    )
}
